import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import { nodeIsImage } from "@corca/tiptap";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { collapseLabelText, isCollapsed, isCollapsible } from "@/components/tiptap/collapse/utils";
import { LockBlockButton } from "@/components/tiptap/lock/LockBlockButton";
import { isLockable, isLocked } from "@/components/tiptap/lock/utils";
import { openSelectionPromptForLine } from "@/components/tiptap/selection-prompt/openSelectionPrompt";
import { insertInlineExpressionAndFocus } from "@/components/tiptap/tiptap-expression/utils";
import { downloadImage } from "@/components/tiptap/utils/downloadImage";
import { duplicateTiptapLineAtPos } from "@/components/tiptap/utils/duplicateTiptapLine";
import { copyLineAtPos, cutLineAtPos } from "@/components/tiptap/utils/lineClipboard";
import { resolveUploadScope } from "@/components/tiptap/utils/useUploadScopes";
import { displayHotkey } from "@/utils/hotkeys";
import { twMerge } from "@/utils/twMerge";

import type { LineMenuProps } from "./LineWrapperView";
import type { DropdownItem, DropdownItemRenderProps } from "@/components/sidebar/Dropdown";
import type { ImageNodeAttrs } from "@corca/tiptap";
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { ComponentProps, ReactNode } from "react";

type Props = LineMenuProps & {
  anchor?: ComponentProps<typeof Dropdown>["anchor"];
  buttonClassName?: string;
  toggleButtonComponent?: ReactNode;
  toggleButtonLabel?: string;
};

function acceptsInlineExpression(node: PMNode | null | undefined): boolean {
  const inlineExpression = node?.type.schema.nodes.inlineExpression;

  return (
    !!node &&
    !!inlineExpression &&
    !!node.contentMatchAt(node.childCount).matchType(inlineExpression)
  );
}

// Text, expressions and blocks like images are all leaves; a line without one has nothing to ask about
function isEmptyLine(node: PMNode): boolean {
  if (node.isLeaf) {
    return false;
  }

  let hasLeaf = false;
  node.descendants((child) => {
    hasLeaf ||= child.isLeaf;
    return !hasLeaf;
  });
  return !hasLeaf;
}

function getLineUrl(tiptap: Editor, pos: number): string | null {
  const domNode = tiptap.view.nodeDOM(pos);
  if (!(domNode instanceof Element)) return null;

  const lineNumberEl = domNode.querySelector(".line-number");
  if (!lineNumberEl) return null;

  const allLines = Array.from(document.querySelectorAll(".line-number"));
  const index = allLines.indexOf(lineNumberEl);
  if (index === -1) return null;

  const { origin, pathname } = window.location;
  return `${origin}${pathname}#L${index + 1}`;
}

const DefaultLineOptionsDropdown = ({
  doc,
  getPos,
  onOpenChange,
  anchor,
  buttonClassName,
  toggleButtonComponent,
  toggleButtonLabel,
  toggleButtonProps,
}: Props) => {
  const { tiptap } = doc.editor;
  const t = useTranslations();
  const tiptapT = useTranslations("tiptap");
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };
  const closeRef = useRef<(() => void) | null>(null);

  const pos = getPos();
  const node = pos !== undefined ? tiptap?.state.doc.nodeAt(pos) : null;
  const isImage = nodeIsImage(node ?? undefined);

  const saveImage = useCallback(() => {
    const imageNode = tiptap?.state.doc.nodeAt(getPos() ?? -1);
    if (!imageNode || !nodeIsImage(imageNode)) return;
    const attrs = imageNode.attrs as ImageNodeAttrs;
    void downloadImage(resolveUploadScope(doc, undefined, attrs), attrs);
  }, [doc, getPos, tiptap]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "d" && doc.editable) {
        e.preventDefault();
        duplicateTiptapLineAtPos(doc, tiptap, getPos());
        closeRef.current?.();
      } else if (e.key === "s" && isImage) {
        e.preventDefault();
        saveImage();
        closeRef.current?.();
      } else if (e.key === "c" && isImage) {
        e.preventDefault();
        void copyLineAtPos(tiptap, getPos());
        closeRef.current?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, doc, getPos, tiptap, isImage, saveImage]);

  // The menu stays mounted across toggles, so read the lock state live from the editor.
  const subscribeToTransactions = useCallback(
    (onChange: () => void) => {
      tiptap?.on("transaction", onChange);
      return () => {
        tiptap?.off("transaction", onChange);
      };
    },
    [tiptap],
  );
  const locked = useSyncExternalStore(subscribeToTransactions, () => {
    const nodePos = getPos();
    return isLocked(nodePos !== undefined ? tiptap?.state.doc.nodeAt(nodePos) : null);
  });

  const copyItem: DropdownItem = {
    title: isImage ? t("tiptap.copyImage") : t("suggestions.clipboardCommand.copy"),
    icon: <Icon name="Copy" className="w-5 h-5" />,
    hotkey: isImage ? displayHotkey("copy") : undefined,
    onClick: () => {
      void copyLineAtPos(tiptap, getPos());
    },
    testId: "line-options-dropdown-copy",
  };
  const cutItems: DropdownItem[] =
    doc.editable && !locked
      ? [
          {
            title: t("suggestions.clipboardCommand.cut"),
            icon: <Icon name="Cut" className="w-5 h-5" />,
            onClick: () => {
              cutLineAtPos(doc, tiptap, getPos);
            },
            testId: "line-options-dropdown-cut",
          },
        ]
      : [];
  const duplicateItems: DropdownItem[] = doc.editable
    ? [
        {
          title: isImage ? t("tiptap.duplicateImage") : t("tiptap.duplicate"),
          icon: <Icon name="Duplicate" className="w-5 h-5" />,
          hotkey: displayHotkey("duplicate-line"),
          onClick: () => {
            duplicateTiptapLineAtPos(doc, tiptap, getPos());
          },
          testId: "line-options-dropdown-duplicate",
        },
      ]
    : [];

  const items: DropdownItem[] = [
    ...(doc.editable && isLockable(node)
      ? [
          {
            renderItem: (props: DropdownItemRenderProps) => (
              <LockBlockButton
                {...props}
                locked={locked}
                onLockedChange={(nextLocked) => {
                  const nodePos = getPos();
                  if (nodePos !== undefined) {
                    tiptap?.chain().setLocked(nodePos, nextLocked).run();
                  }
                }}
              />
            ),
          },
          { type: "separator" as const },
        ]
      : []),
    // The gutter's collapse toggle is hidden from screen readers, so its action lives here too.
    ...(node && isCollapsible(node)
      ? [
          {
            title: collapseLabelText(isCollapsed(node), node.type.name, tiptapT),
            icon: <Icon name="Collapse" className="w-5 h-5" />,
            hotkey: displayHotkey("collapse-block"),
            onClick: () => {
              const nodePos = getPos();

              if (nodePos !== undefined) {
                // As the gutter toggle does: the block menu goes away with a collapsed block.
                tiptap?.chain().toggleCollapsed(nodePos, true).focus().run();
              }
            },
            testId: "line-options-dropdown-collapse",
          },
          { type: "separator" as const },
        ]
      : []),
    ...(doc.editable && !locked && acceptsInlineExpression(node)
      ? [
          {
            title: t("suggestions.insertMath"),
            icon: <Icon name="InsertMath" className="w-5 h-5" />,
            onClick: () => {
              const nodePos = getPos();

              if (nodePos === undefined || !node) {
                return;
              }

              tiptap?.commands.setTextSelection(nodePos + node.nodeSize - 1);
              insertInlineExpressionAndFocus(doc);
            },
            testId: "line-options-dropdown-insert-math",
          },
          { type: "separator" as const },
        ]
      : []),
    ...(isImage ? [...duplicateItems, copyItem] : [copyItem, ...cutItems, ...duplicateItems]),
    { type: "separator" as const },
    ...(isImage
      ? [
          {
            title: t("tiptap.saveImage"),
            icon: <Icon name="download/Download" className="w-5 h-5" />,
            hotkey: displayHotkey("save-image"),
            onClick: saveImage,
            testId: "line-options-dropdown-save-image",
          },
        ]
      : []),
    {
      title: t("tiptap.copyBlockLink"),
      icon: <Icon name="Link" className="w-5 h-5" />,
      onClick: async () => {
        const pos = getPos();
        if (pos === undefined || !tiptap) return;
        const url = getLineUrl(tiptap, pos);
        if (url) await navigator.clipboard.writeText(url);
      },
      testId: "line-options-dropdown-copylink",
    },
    ...(doc.editable && node && !isEmptyLine(node)
      ? [
          { type: "separator" as const },
          {
            title: t("suggestions.selectionPrompt"),
            icon: <Icon name="AiHelper" className="w-5 h-5" />,
            onClick: () => {
              const nodePos = getPos();
              if (nodePos !== undefined) {
                openSelectionPromptForLine(doc, nodePos);
              }
            },
            testId: "line-options-dropdown-ask-ai",
          },
        ]
      : []),
  ];

  return (
    <Dropdown
      portal
      // The block controls manage the editor's inertness around this menu; a modal menu would
      // make the editor inert too, and restore that when it closes.
      modal={false}
      anchor={anchor ?? { to: "bottom", padding: "4px", gap: "4px" }}
      className={
        buttonClassName ??
        twMerge(
          "line-option-button flex items-center justify-center rounded-md w-[28px] h-[28px] mt-[3px] ml-[5px]",
          "hover:bg-blue-100 dark:hover:bg-blue-900",
          "select-none selection:bg-transparent",
        )
      }
      toggleButtonComponent={
        toggleButtonComponent ?? <Icon name="More" className="w-[20px] h-[20px]" />
      }
      toggleButtonLabel={toggleButtonLabel ?? t("tiptap.lineOptions")}
      toggleButtonProps={toggleButtonProps}
      items={items}
      onOpenChange={handleOpenChange}
      closeRef={closeRef}
    />
  );
};

export default DefaultLineOptionsDropdown;
